import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Trash2 } from 'lucide-react';

const DataDeletionPage = () => {
  useEffect(() => {
    document.title = 'Data Deletion | XtraGlass';
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a0b]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <motion.a
            href="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            whileHover={{ x: -4 }}
          >
            <ArrowLeft size={20} />
            <span className="font-mono text-sm">Back to Home</span>
          </motion.a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center">
              <Trash2 size={32} className="text-red-400" />
            </div>
            <div>
              <h1 className="font-display text-4xl md:text-5xl font-bold">
                Data Deletion
              </h1>
              <p className="text-white/50">Request to delete your XtraGlass data</p>
            </div>
          </div>

          <div className="prose prose-invert prose-lg max-w-none">
            <div className="space-y-8 text-white/70">
              <div className="bg-white/5 border border-white/10 rounded-xl p-8">
                <h2 className="text-2xl font-display font-bold text-white mb-4">How to Delete Your Data</h2>
                <p className="mb-6">
                  If you want to delete your XtraGlass account and all associated data, you can do so by following these steps:
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-[#ff84e4]/10 flex items-center justify-center text-[#ff84e4] font-bold flex-shrink-0">1</span>
                    <div>
                      <h3 className="text-white font-semibold">Open the XtraGlass App</h3>
                      <p className="text-white/60">Launch the XtraGlass application on your device</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-[#ff84e4]/10 flex items-center justify-center text-[#ff84e4] font-bold flex-shrink-0">2</span>
                    <div>
                      <h3 className="text-white font-semibold">Go to Profile</h3>
                      <p className="text-white/60">Navigate to your profile section in the app</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-[#ff84e4]/10 flex items-center justify-center text-[#ff84e4] font-bold flex-shrink-0">3</span>
                    <div>
                      <h3 className="text-white font-semibold">Access Settings</h3>
                      <p className="text-white/60">Open the settings menu from your profile</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-[#ff84e4]/10 flex items-center justify-center text-[#ff84e4] font-bold flex-shrink-0">4</span>
                    <div>
                      <h3 className="text-white font-semibold">Request Data Deletion</h3>
                      <p className="text-white/60">Select &quot;Delete Account&quot; or &quot;Delete My Data&quot; option and confirm your request</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-6">
                <h3 className="text-lg font-display font-bold text-yellow-400 mb-2">Alternative Method</h3>
                <p className="text-white/70">
                  If you&apos;re unable to access the app, you can also request data deletion by sending an email to{' '}
                  <a href="mailto:xtraglassindia@gmail.com" className="text-[#ff84e4] hover:underline">
                    xtraglassindia@gmail.com
                  </a>{' '}
                  with the subject line &quot;Data Deletion Request&quot; and include your registered email address or phone number.
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-display font-bold text-white mb-4">What Data Will Be Deleted?</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-[#ff84e4]">•</span>
                    Your account information (name, email, phone number)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#ff84e4]">•</span>
                    Your profile data and preferences
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#ff84e4]">•</span>
                    Your booking history
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#ff84e4]">•</span>
                    Your saved venues and favorites
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#ff84e4]">•</span>
                    Any content you've posted or shared
                  </li>
                </ul>
              </div>

              <p className="text-white/50 text-sm">
                Please note that data deletion is permanent and cannot be undone. Once your data is deleted, you will not be able to recover your account or any associated information. The deletion process may take up to 30 days to complete.
              </p>

              <h2 className="text-2xl font-display font-bold text-white mt-12">Contact Us</h2>
              <p>If you have any questions about data deletion, you can contact us:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>By email: <a href="mailto:xtraglassindia@gmail.com" className="text-[#ff84e4] hover:underline">xtraglassindia@gmail.com</a></li>
                <li>Website: <a href="https://www.xtraglass.com" className="text-[#ff84e4] hover:underline" target="_blank" rel="noopener noreferrer">www.xtraglass.com</a></li>
              </ul>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-white/40 text-sm">
          © {new Date().getFullYear()} XtraGlass. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default DataDeletionPage;